import { useEffect, useRef, useState } from 'react';
import { Download, Film, LoaderCircle, LockKeyhole, QrCode, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import type { AccountUser, Wallpaper } from '../types';
import { downloadWallpaper, type DownloadFormat } from '../lib/downloadWallpaper';
import { supabase } from '../lib/supabase';

type Payment = {
  provider: 'sandbox' | 'wechat' | 'alipay' | 'personal';
  url?: string;
  qrUrl?: string;
  channel?: 'wechat' | 'alipay';
  amountMinor?: number;
  expiresIn?: number;
};

type Order = {
  orderId: string;
  recoveryCode?: string;
  payment: Payment | null;
  status?: string;
};

const MESSAGES: Record<string, string> = {
  PAYMENT_UNAVAILABLE: '支付通道未启用，请稍后再试。',
  ARTWORK_UNAVAILABLE: '这件作品暂不可购买，请刷新后重试。',
  ARTWORK_NOT_PAID: '这件作品不是付费作品，请直接领取。',
  EMAIL_REQUIRED: '账号缺少邮箱，请先补充邮箱。',
  EMAIL_NOT_VERIFIED: '请先完成邮箱验证，再创建订单。',
  INVALID_REQUEST: '请填写有效的邮箱地址。',
  GUEST_EXPIRED: '游客凭证已失效，请刷新页面后重试。',
  IDEMPOTENCY_CONFLICT: '订单信息与上一次不一致，请刷新页面后重试。',
  ENVIRONMENT_MISMATCH: '订单环境不一致，请刷新后重试。',
  ORDER_CLOSED: '订单已关闭，无法继续支付。',
  NOT_ENTITLED: '当前浏览器不是该订单的创建者。',
  CREATE_FAILED: '创建订单失败，请稍后重试。',
  PAYMENT_FAILED: '支付请求失败，请稍后重试。',
  UPSTREAM: '后端暂时不可用，请稍后重试。',
  CONFIG: '服务器尚未配置支付或 Supabase，请联系管理员。',
  CHANNEL_MISMATCH: '支付方式与当前通道不一致，请刷新后重试。',
  WECHAT_CREATE_FAILED: '微信支付下单失败，请稍后再试。',
  ALIPAY_CREATE_FAILED: '支付宝下单失败，请稍后再试。',
  QR_NOT_CONFIGURED: '个人收款码尚未配置，请联系管理员。',
  REPORT_REQUIRED: '请至少填写付款人、交易单号或备注中的一项。',
  REPORT_REJECTED: '该订单的付款信息未通过核对，请联系管理员。',
  REPORT_CONFLICT: '该订单已提交过付款信息，请等待管理员确认。',
  ORDER_NOT_PENDING: '订单状态已变化，请刷新页面。',
  NOT_PERSONAL_ORDER: '该订单不是个人收款码订单。',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function authHeaders(): Promise<Record<string, string>> {
  if (!supabase) return {};
  const session = await supabase.auth.getSession().catch(() => null);
  const token = session?.data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function messageOf(error: unknown): string {
  const message = (error as Error)?.message ?? '';
  return MESSAGES[message] || message || '操作失败，请稍后重试。';
}

/**
 * Checkout for the new Pages order APIs. Sandbox mode never touches a real
 * provider; official live mode renders a WeChat Native QR code or redirects to
 * Alipay and polls the signed webhook. Personal QR mode shows a collection code
 * and submits a payment report that an admin reviews manually.
 */
export function Checkout({
  item,
  user,
  mode,
  onCancel,
  onPaid,
  notify,
}: {
  item: Wallpaper;
  user: AccountUser | null;
  mode: 'sandbox' | 'live';
  onCancel: () => void;
  onPaid: () => Promise<void>;
  notify: (message: string) => void;
}) {
  const [email, setEmail] = useState(user?.email ?? '');
  const [channel, setChannel] = useState<'wechat' | 'alipay'>('wechat');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [order, setOrder] = useState<Order | null>(null);
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState<'create' | 'pay' | 'report' | 'poll' | DownloadFormat | ''>('');
  const [error, setError] = useState('');
  const [reportStatus, setReportStatus] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [payerName, setPayerName] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [reportNote, setReportNote] = useState('');
  const onPaidRef = useRef(onPaid);
  const notifyRef = useRef(notify);
  useEffect(() => {
    onPaidRef.current = onPaid;
    notifyRef.current = notify;
  }, [onPaid, notify]);

  useEffect(() => {
    if (mode !== 'live' || !order || paid) return;
    let cancelled = false;
    let timer: number | undefined;
    const startedAt = Date.now();

    async function check() {
      if (!order || cancelled) return;
      if (Date.now() - startedAt > 15 * 60 * 1000) {
        setError('支付确认超时，请刷新页面或使用订单号找回。');
        return;
      }
      const headers = await authHeaders();
      const response = await fetch(`/api/orders/${order.orderId}?environment=live`, {
        credentials: 'same-origin',
        headers: order.recoveryCode
          ? { ...headers, 'X-Mujian-Recovery': order.recoveryCode }
          : headers,
      }).catch(() => null);
      if (cancelled) return;
      if (response?.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          status?: string;
          reportStatus?: string;
        };
        if (data.reportStatus) setReportStatus(data.reportStatus);
        if (data.status === 'paid') {
          setPaid(true);
          await onPaidRef.current();
          notifyRef.current('支付已确认，权益已写入，可以下载原图。');
          return;
        }
        if (data.status === 'closed' || data.status === 'refunded') {
          setError(MESSAGES.ORDER_CLOSED);
          return;
        }
      }
      timer = window.setTimeout(check, 4000);
    }
    void check();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [mode, order, paid]);

  async function createOrder() {
    if (!user && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(MESSAGES.INVALID_REQUEST);
      return;
    }
    setBusy('create');
    setError('');
    try {
      const response = await fetch('/api/orders', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...(await authHeaders()),
        },
        body: JSON.stringify({
          artworkId: item.id,
          channel,
          ...(user ? {} : { email: email.trim() }),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'CREATE_FAILED');
      if (data.status === 'paid') {
        setPaid(true);
        await onPaid();
        return;
      }
      if (
        !data?.payment ||
        (typeof data.payment.url !== 'string' && typeof data.payment.qrUrl !== 'string')
      )
        throw new Error('CREATE_FAILED');
      if (data.reportStatus) setReportStatus(data.reportStatus);
      setOrder(data as Order);
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy('');
    }
  }

  async function pay() {
    if (!order?.payment?.url) return;
    setBusy('pay');
    setError('');
    try {
      const response = await fetch(order.payment.url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: await authHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'PAYMENT_FAILED');
      setPaid(true);
      await onPaid();
      notify('sandbox 支付完成，未产生真实扣款。');
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy('');
    }
  }

  async function openAlipay() {
    if (!order?.payment?.url) return;
    window.location.assign(order.payment.url);
  }

  async function submitReport() {
    if (!order || reportStatus) return;
    if (!payerName.trim() && !transactionRef.trim() && !reportNote.trim()) {
      setError('请至少填写付款人、交易单号或备注中的一项。');
      return;
    }
    setBusy('report');
    setError('');
    try {
      const headers = await authHeaders();
      const response = await fetch(`/api/orders/${order.orderId}/report`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(order.recoveryCode ? { 'X-Mujian-Recovery': order.recoveryCode } : {}),
          ...headers,
        },
        body: JSON.stringify({
          payerName: payerName.trim(),
          transactionRef: transactionRef.trim(),
          note: reportNote.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'REPORT_FAILED');
      setReportStatus(data.status || 'pending');
      setReportOpen(false);
      notify('付款信息已提交，管理员核对后会解锁下载。');
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy('');
    }
  }

  async function refreshStatus() {
    if (!order) return;
    setBusy('poll');
    setError('');
    try {
      const headers = await authHeaders();
      const response = await fetch(`/api/orders/${order.orderId}?environment=live`, {
        credentials: 'same-origin',
        headers: order.recoveryCode
          ? { ...headers, 'X-Mujian-Recovery': order.recoveryCode }
          : headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'UPSTREAM');
      if (data.reportStatus) setReportStatus(data.reportStatus);
      if (data.status === 'paid') {
        setPaid(true);
        await onPaid();
        notify('支付已确认，权益已写入，可以下载原图。');
      } else {
        notify('支付尚未确认，请稍候。');
      }
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy('');
    }
  }

  async function download(format: DownloadFormat) {
    setBusy(format);
    setError('');
    try {
      await downloadWallpaper(item, format);
      notify('文件已交给浏览器保存，请查看下载记录。');
    } catch (reason) {
      setError(messageOf(reason));
    } finally {
      setBusy('');
    }
  }

  const sandbox = mode === 'sandbox';
  const personal = order?.payment?.provider === 'personal';
  const qr = order?.payment?.provider === 'wechat' ? (order.payment.url ?? '') : '';
  const amount =
    personal && typeof order?.payment?.amountMinor === 'number'
      ? (order.payment.amountMinor / 100).toFixed(2)
      : item.price;

  return (
    <div className={`checkout-panel ${sandbox ? 'sandbox-checkout' : 'live-checkout'}`}>
      <div>
        <strong>
          {paid
            ? sandbox
              ? 'sandbox 支付完成'
              : '支付完成'
            : order
              ? '完成支付'
              : sandbox
                ? 'sandbox 测试结算'
                : '结算'}
        </strong>
        <button aria-label="关闭结算" onClick={onCancel}>
          <X size={17} />
        </button>
      </div>
      {!order && (
        <>
          <p>
            {sandbox
              ? '这是新订单链路的测试流程，只创建 sandbox 订单，不会产生真实扣款。'
              : '订单创建后会打开所选支付方式，支付结果由支付平台异步通知确认。'}
          </p>
          <div className="checkout-total">
            <span>{item.title} · 个人授权</span>
            <strong>¥{item.price}</strong>
          </div>
          {user ? (
            <p className="sandbox-hint">订单将绑定账号 {user.email}，不需要恢复码。</p>
          ) : (
            <label className="sandbox-field">
              接收邮箱
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={busy === 'create'}
              />
            </label>
          )}
          <label className="sandbox-field">
            支付方式
            <select
              value={channel}
              onChange={(event) => setChannel(event.target.value as 'wechat' | 'alipay')}
              disabled={busy === 'create'}
            >
              <option value="wechat">微信支付{sandbox ? '（sandbox）' : ''}</option>
              <option value="alipay">支付宝{sandbox ? '（sandbox）' : ''}</option>
            </select>
          </label>
          <button
            className="button button-dark full-button"
            disabled={busy === 'create'}
            onClick={createOrder}
          >
            {busy === 'create' ? (
              <LoaderCircle size={17} className="spin" />
            ) : (
              <LockKeyhole size={16} />
            )}
            {sandbox ? '创建测试订单' : '创建订单'}
          </button>
        </>
      )}
      {order && !paid && (
        <>
          <p>
            订单号 <code>{order.orderId}</code>
          </p>
          {order.recoveryCode ? (
            <>
              <p>恢复码只显示这一次，请先保存：</p>
              <code className="sandbox-code">{order.recoveryCode}</code>
              <p className="sandbox-hint">换设备或清除 Cookie 后，凭订单号和恢复码找回。</p>
            </>
          ) : (
            <p className="sandbox-hint">订单已绑定账号，登录后即可找回。</p>
          )}
          {!sandbox && personal && order.payment?.qrUrl && (
            <div className="checkout-personal">
              <p>
                请使用
                {order.payment.channel === 'alipay' ? '支付宝' : '微信'}扫描下方个人收款码，支付
                <strong> ¥{amount} </strong>
                并在付款备注中填写订单号后 8 位。
              </p>
              <img
                className="checkout-personal-qr"
                src={order.payment.qrUrl}
                alt={`${order.payment.channel === 'alipay' ? '支付宝' : '微信'}个人收款码`}
              />
              <p className="sandbox-hint">
                订单号 <code>{order.orderId}</code>
              </p>
              {reportStatus === 'pending' && (
                <p className="sandbox-hint">
                  <LoaderCircle size={15} className="spin" />
                  付款信息已提交，管理员核对后自动解锁。请保留付款记录。
                </p>
              )}
              {reportStatus === 'approved' && (
                <p className="sandbox-hint">付款已确认，正在写入权益，请稍候刷新。</p>
              )}
              {reportStatus === 'rejected' && (
                <p className="inline-error">
                  付款信息未通过核对，请确认金额和订单备注后联系管理员。
                </p>
              )}
              {!reportStatus &&
                (reportOpen ? (
                  <div className="checkout-report">
                    <label className="sandbox-field">
                      付款人姓名
                      <input
                        value={payerName}
                        onChange={(event) => setPayerName(event.target.value)}
                        placeholder="可选"
                        disabled={busy === 'report'}
                      />
                    </label>
                    <label className="sandbox-field">
                      交易单号 / 付款截图说明
                      <input
                        value={transactionRef}
                        onChange={(event) => setTransactionRef(event.target.value)}
                        placeholder="支付宝/微信账单中的单号，可选"
                        disabled={busy === 'report'}
                      />
                    </label>
                    <label className="sandbox-field">
                      备注
                      <input
                        value={reportNote}
                        onChange={(event) => setReportNote(event.target.value)}
                        placeholder="例如付款时间、金额后四位"
                        disabled={busy === 'report'}
                      />
                    </label>
                    <button
                      className="button button-dark full-button"
                      disabled={busy === 'report'}
                      onClick={submitReport}
                    >
                      {busy === 'report' ? (
                        <LoaderCircle size={17} className="spin" />
                      ) : (
                        <LockKeyhole size={16} />
                      )}
                      提交付款信息
                    </button>
                    <button
                      className="button button-outline full-button"
                      disabled={busy === 'report'}
                      onClick={() => setReportOpen(false)}
                    >
                      取消
                    </button>
                  </div>
                ) : (
                  <button
                    className="button button-dark full-button"
                    onClick={() => setReportOpen(true)}
                  >
                    我已支付
                  </button>
                ))}
            </div>
          )}
          {sandbox && order.payment && (
            <button
              className="button button-dark full-button"
              disabled={busy === 'pay'}
              onClick={pay}
            >
              {busy === 'pay' ? (
                <LoaderCircle size={17} className="spin" />
              ) : (
                <LockKeyhole size={16} />
              )}
              模拟支付
            </button>
          )}
          {!sandbox && qr && (
            <div className="checkout-qr">
              <QRCodeSVG value={qr} size={176} marginSize={2} />
              <p className="sandbox-hint">
                <QrCode size={15} /> 请使用微信扫描二维码完成支付
              </p>
            </div>
          )}
          {!sandbox && order.payment?.provider === 'alipay' && (
            <button className="button button-dark full-button" onClick={openAlipay}>
              <LockKeyhole size={16} />
              前往支付宝支付
            </button>
          )}
          {!sandbox && (
            <div className="checkout-poll">
              <span>
                <LoaderCircle size={15} className="spin" />{' '}
                {personal ? '等待管理员确认付款' : '正在等待支付确认'}
              </span>
              <button
                className="button button-outline"
                disabled={busy === 'poll'}
                onClick={refreshStatus}
              >
                刷新状态
              </button>
            </div>
          )}
        </>
      )}
      {paid && (
        <>
          <p>
            {user ? '权益已写入你的账号，可以下载原图。' : '权益已写入当前游客身份，可以下载原图。'}
          </p>
          <button
            className="button button-dark full-button"
            disabled={busy === 'png'}
            onClick={() => download('png')}
          >
            {busy === 'png' ? <LoaderCircle size={17} className="spin" /> : <Download size={17} />}
            下载高清 PNG
          </button>
          {item.type === 'dynamic' && (
            <button
              className="button button-outline full-button"
              disabled={busy === 'mp4'}
              onClick={() => download('mp4')}
            >
              {busy === 'mp4' ? <LoaderCircle size={17} className="spin" /> : <Film size={16} />}
              下载 MP4 素材
            </button>
          )}
        </>
      )}
      {error && (
        <div className="inline-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
